import { useState } from "react";

import {
  AssetsService,
  type AssetBaseResponse,
  type AssetTransitionPayload,
  type LifecycleState,
} from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { LIFECYCLE_STATES } from "@/lib/asset-enums";

// Lifecycle values map to translation keys so the stage labels
// follow the active locale.
const LIFECYCLE_KEY: Record<LifecycleState, TranslationKey> = {
  draft: "assets.status.draft",
  review: "assets.status.review",
  active: "assets.status.active",
  deprecated: "assets.status.deprecated",
  retired: "assets.status.retired",
};
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";

interface AssetTransitionDialogProps {
  asset: AssetBaseResponse | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function AssetTransitionDialog({
  asset,
  onClose,
  onSuccess,
}: AssetTransitionDialogProps) {
  const { t } = useTranslation();
  const [toState, setToState] = useState<LifecycleState>("review");
  const [reason, setReason] = useState("");

  const { mutate, isLoading, error, reset } = useMutation<
    AssetTransitionPayload,
    AssetBaseResponse
  >((body) => AssetsService.transition(asset!.id, body), {
    onSuccess: () => {
      setReason("");
      onSuccess();
    },
  });

  if (!asset) return null;

  const handleClose = () => {
    reset();
    setReason("");
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const body: AssetTransitionPayload = { to_state: toState };
    const trimmed = reason.trim();
    if (trimmed) body.reason = trimmed;
    await mutate(body);
  };

  const targets = LIFECYCLE_STATES.filter(
    (state) => state !== asset.lifecycle_state
  );

  return (
    <Dialog open onClose={handleClose} size="max-w-md">
      <DialogHeader>
        <DialogTitle>{t("assets.transitionDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("assets.transitionDialog.descriptionBefore")}
          <strong className="text-ocean-900">{asset.name}</strong>
          {t("assets.transitionDialog.descriptionMiddle")}
          <strong className="text-ocean-900">
            {t(LIFECYCLE_KEY[asset.lifecycle_state])}
          </strong>
          {t("assets.transitionDialog.descriptionAfter")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div>
            <span className="mb-1.5 block text-sm font-medium text-ocean-900">
              {t("assets.transitionDialog.targetLabel")}
            </span>
            <div className="flex flex-wrap gap-2">
              {targets.map((state) => (
                <button
                  key={state}
                  type="button"
                  onClick={() => setToState(state)}
                  aria-pressed={toState === state}
                  className={
                    "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors " +
                    (toState === state
                      ? "border-ocean-600 bg-ocean-50 text-ocean-900"
                      : "border-ocean-100 bg-white text-neutral-600 hover:border-ocean-200")
                  }
                >
                  {t(LIFECYCLE_KEY[state])}
                </button>
              ))}
            </div>
          </div>

          <FormField
            label={t("assets.transitionDialog.reasonLabel")}
            value={reason}
            onChange={setReason}
            placeholder={t("assets.transitionDialog.reasonPlaceholder")}
            maxLength={500}
            hint={t("assets.transitionDialog.reasonHint")}
          />

          {error && <Alert variant="danger">{parseApiError(error)}</Alert>}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("assets.transitionDialog.cancel")}
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading
              ? t("assets.transitionDialog.submitLoading")
              : t("assets.transitionDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
